import { Helmet } from 'react-helmet-async';
import { CONTACT, AREAS_SERVED } from '@/config/contact';

const StructuredData = () => {
  const sameAs = [CONTACT.instagramUrl, CONTACT.linkedinUrl, CONTACT.googleBusinessUrl];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Aivinity",
    "alternateName": "JC Productions",
    "url": "https://aivinity.de",
    "logo": "https://aivinity.de/aivinity-logo-dark.png",
    "description": "Aivinity (ehemals JC Productions) – Professionelle Videoproduktion, Imagefilme und Webseiten in Aachen & NRW.",
    "telephone": CONTACT.phone,
    "email": CONTACT.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CONTACT.street,
      "postalCode": CONTACT.postalCode,
      "addressLocality": CONTACT.city,
      "addressRegion": CONTACT.region,
      "addressCountry": CONTACT.country
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": CONTACT.phone,
      "email": CONTACT.email,
      "contactType": "customer service",
      "areaServed": "DE",
      "availableLanguage": ["German", "English"]
    },
    "sameAs": sameAs
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Aivinity – Videoproduktion & Imagefilm Aachen",
    "image": "https://aivinity.de/aivinity-logo-dark.png",
    "description": "Aivinity (ehemals JC Productions) – Videoproduktion, Imagefilm, Testimonials, KI-Filme, Reels & Social Media Management in Aachen und ganz NRW.",
    "@id": "https://aivinity.de",
    "url": "https://aivinity.de",
    "telephone": CONTACT.phone,
    "email": CONTACT.email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": CONTACT.street,
      "postalCode": CONTACT.postalCode,
      "addressLocality": CONTACT.city,
      "addressRegion": CONTACT.region,
      "addressCountry": CONTACT.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 50.7753,
      "longitude": 6.0839
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "€€",
    "sameAs": sameAs,
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "12",
      "bestRating": "5",
      "worstRating": "1"
    },
    "areaServed": AREAS_SERVED.map((name) => ({
      "@type": name === "Nordrhein-Westfalen" ? "State" : "City",
      name,
    })),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Videoproduktion & Webdesign Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Imagefilm Produktion",
            "description": "Professionelle Imagefilme für Unternehmen in Aachen"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Videoproduktion",
            "description": "Klassische und KI-gestützte Videoproduktion"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Webseiten Erstellung",
            "description": "Günstige professionelle Webseiten ab 1.490€"
          }
        }
      ]
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Aivinity",
    "alternateName": "JC Productions",
    "url": "https://aivinity.de",
    "description": "Aivinity – Videoproduktion & Webseitenagentur Aachen & NRW"
  };

  const navigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Hauptnavigation",
    "hasPart": [
      {
        "@type": "SiteNavigationElement",
        "name": "Videoproduktionen",
        "description": "Professionelle Videoproduktion in Aachen & NRW",
        "url": "https://aivinity.de/"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Webseiten ab 1490€",
        "description": "Günstige professionelle Webseiten in 7 Tagen",
        "url": "https://web.aivinity.de"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Imagefilm",
        "description": "Professionelle Imagefilme aus Aachen für NRW",
        "url": "https://aivinity.de/imagefilm-aachen"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Testimonials",
        "description": "Authentische Testimonial-Videos für mehr Vertrauen",
        "url": "https://aivinity.de/testimonials"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "KI-gestützte Videoproduktion",
        "description": "KI-Videos ohne Drehtag und Betriebsunterbrechung",
        "url": "https://aivinity.de/ki-videoproduktion"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Reels",
        "description": "Kurzformat-Videos mit Hook, Rhythmus und Wiedererkennung",
        "url": "https://aivinity.de/reels"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Social Media Management",
        "description": "Reichweite, viraler Content und Markensichtbarkeit",
        "url": "https://aivinity.de/social-media"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Über uns",
        "description": "Lernen Sie unser Team kennen",
        "url": "https://aivinity.de/ueber-uns"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "FAQ",
        "description": "Häufig gestellte Fragen",
        "url": "https://aivinity.de/faq"
      },
      {
        "@type": "SiteNavigationElement",
        "name": "Termin buchen",
        "description": "Kostenloses Beratungsgespräch vereinbaren",
        "url": "https://aivinity.de/termin"
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(navigationSchema)}
      </script>
    </Helmet>
  );
};

export default StructuredData;
